/**
 * SkeletonLoader Component
 * Feature: 009-provider-ui-pixel-perfect
 * 
 * Skeleton loading placeholders for improved perceived performance
 * Supports multiple variants for different content types
 */

import { cn } from '@/lib/utils';

type SkeletonVariant = 'text' | 'card' | 'image' | 'service-card' | 'badge';

type SkeletonLoaderProps = {
  /** Skeleton variant for different content types */
  variant: SkeletonVariant;
  /** Custom width (CSS value) */
  width?: string;
  /** Custom height (CSS value) */
  height?: string;
  /** Aspect ratio for image variant (e.g., "16/9", "4/3", "1/1") */
  aspectRatio?: string;
  /** Additional CSS classes */
  className?: string;
  /** Number of skeletons to render */
  count?: number;
  /** Aria label for accessibility */
  ariaLabel?: string;
  /** Enable/disable animation */
  animate?: boolean;
};

/**
 * SkeletonLoader Component
 * Displays animated skeleton placeholders while content loads
 */
export const SkeletonLoader = ({
  variant,
  width,
  height,
  aspectRatio,
  className,
  count = 1,
  ariaLabel = 'Loading...',
  animate = true,
}: SkeletonLoaderProps) => {
  const baseClasses = cn(
    'bg-gray-200 dark:bg-gray-700',
    animate && 'animate-pulse'
  );

  const getVariantStyles = (): string => {
    switch (variant) {
      case 'text':
        return cn(
          baseClasses,
          'h-4 rounded',
          className
        );
      
      case 'card':
        return cn(
          baseClasses,
          'rounded-lg p-4',
          !height && 'h-32',
          className
        );
      
      case 'image':
        return cn(
          baseClasses,
          'rounded-md',
          aspectRatio ? `aspect-[${aspectRatio}]` : 'aspect-video',
          className
        );
      
      case 'service-card':
        return cn(
          baseClasses,
          'rounded-lg',
          !height && 'h-40',
          className
        );
      
      case 'badge':
        return cn(
          baseClasses,
          'rounded-full',
          !height && 'h-6',
          !width && 'w-20',
          className
        );
      
      default:
        return cn(baseClasses, className);
    }
  };

  const renderSkeleton = (index: number) => {
    const style: React.CSSProperties = {};
    
    if (width) style.width = width;
    if (height) style.height = height;

    // For service-card variant, render multiple elements
    if (variant === 'service-card') {
      return (
        <div
          key={index}
          className={cn(baseClasses, 'rounded-lg p-6 space-y-3')}
          style={style}
          aria-label={ariaLabel}
          aria-busy="true"
        >
          {/* Icon skeleton */}
          <div className={cn(baseClasses, 'h-10 w-10 rounded-md')} />
          
          {/* Title skeleton */}
          <div className={cn(baseClasses, 'h-5 w-3/4 rounded')} />
          
          {/* Description skeletons */}
          <div className="space-y-2">
            <div className={cn(baseClasses, 'h-4 w-full rounded')} />
            <div className={cn(baseClasses, 'h-4 w-5/6 rounded')} />
          </div>
          
          {/* Price skeleton */}
          <div className={cn(baseClasses, 'h-6 w-24 rounded')} />
        </div>
      );
    }

    return (
      <div
        key={index}
        className={getVariantStyles()}
        style={style}
        aria-label={ariaLabel}
        aria-busy="true"
      />
    );
  };

  // Render multiple skeletons if count > 1
  if (count > 1) {
    return (
      <div className="space-y-2">
        {Array.from({ length: count }, (_, index) => renderSkeleton(index))}
      </div>
    );
  }

  return renderSkeleton(0);
};

/**
 * Compound component for common skeleton patterns
 */
export const SkeletonGroup = {
  /** Provider card skeleton (image + details) */
  ProviderCard: () => (
    <div className="border rounded-lg p-4 space-y-3">
      <SkeletonLoader variant="image" aspectRatio="16/9" />
      <SkeletonLoader variant="text" width="60%" height="24px" />
      <SkeletonLoader variant="text" count={2} />
      <div className="flex gap-2 mt-2">
        <SkeletonLoader variant="badge" />
        <SkeletonLoader variant="badge" />
      </div>
    </div>
  ),

  /** Hero section skeleton */
  Hero: () => (
    <div className="space-y-4">
      <SkeletonLoader variant="text" width="40%" height="32px" />
      <SkeletonLoader variant="text" width="60%" height="20px" />
      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <SkeletonLoader variant="image" aspectRatio="16/9" />
        <div className="space-y-3">
          <SkeletonLoader variant="text" count={4} />
        </div>
      </div>
    </div>
  ),

  /** Overview section skeleton */
  Overview: () => (
    <div className="space-y-6">
      <div className="space-y-2">
        <SkeletonLoader variant="text" width="30%" height="24px" />
        <SkeletonLoader variant="text" count={3} />
      </div>
      <div className="space-y-2">
        <SkeletonLoader variant="text" width="30%" height="24px" />
        <SkeletonLoader variant="text" count={4} />
      </div>
    </div>
  ),

  /** Services grid skeleton */
  Services: () => (
    <div className="grid md:grid-cols-2 gap-4">
      <SkeletonLoader variant="service-card" />
      <SkeletonLoader variant="service-card" />
      <SkeletonLoader variant="service-card" />
      <SkeletonLoader variant="service-card" />
    </div>
  ),

  /** Media gallery skeleton */
  MediaGallery: () => (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {Array.from({ length: 6 }, (_, i) => (
        <SkeletonLoader key={i} variant="image" aspectRatio="4/3" />
      ))}
    </div>
  ),

  /** Badges grid skeleton */
  Badges: () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className="border rounded-lg p-4 space-y-2">
          <div className="flex items-center gap-3">
            <SkeletonLoader variant="image" width="40px" height="40px" />
            <SkeletonLoader variant="text" width="150px" height="20px" />
          </div>
          <SkeletonLoader variant="text" count={2} />
        </div>
      ))}
    </div>
  ),
};

